import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useController, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FSelect } from "@ui/components/inputs/FSelect";
import { toast } from "@ui/utils/toaster";
import { apiErrorMessage, clientsApi } from "@api/enpoints/clients-api";
import { categoriesApi } from "@api/enpoints/categories-api";
/**
 * "Client & Categories" of the AP general settings: one client, one of that client's locations, and any number of AP
 * categories. The location list follows the selected client; a location that is not the client's is cleared.
 */
export const ClientCategoriesSection = ({ control, ap }) => {
    var _a;
    const { t } = useTranslation(["common"]);
    const [clientOptions, setClientOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    // Location id -> name of the other access point using it (a location belongs to one access point).
    const [usedBy, setUsedBy] = useState({});
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const locationRequest = useRef(0);
    const clientId = useWatch({ control, name: "clientId" });
    const { field: locationField } = useController({ control, name: "clientLocationId" });
    // The AP's current client / location / categories, so the fields show their names before the lists arrive.
    const seededClients = useMemo(() => { var _a; return ((ap === null || ap === void 0 ? void 0 : ap.clientId) ? [{ value: ap.clientId, label: (_a = ap.clientName) !== null && _a !== void 0 ? _a : "" }] : []); }, [ap === null || ap === void 0 ? void 0 : ap.clientId, ap === null || ap === void 0 ? void 0 : ap.clientName]);
    const seededCategories = useMemo(() => { var _a; return ((_a = ap === null || ap === void 0 ? void 0 : ap.apCategories) !== null && _a !== void 0 ? _a : []).map((c) => ({ value: c.id, label: c.name })); }, [ap === null || ap === void 0 ? void 0 : ap.apCategories]);
    useEffect(() => {
        let active = true;
        Promise.all([clientsApi.getOptions(), categoriesApi.getOptions("ap")])
            .then(([clients, categories]) => {
            var _a, _b;
            if (!active)
                return;
            setClientOptions(((_a = clients.data.data) !== null && _a !== void 0 ? _a : []).map((c) => ({ value: c.id, label: c.name })));
            setCategoryOptions(((_b = categories.data.data) !== null && _b !== void 0 ? _b : []).map((c) => ({ value: c.id, label: c.name })));
        })
            .catch((error) => toast.error(apiErrorMessage(error, t("common:catalog.ap.optionsFailed", "Could not load clients and categories."))));
        return () => {
            active = false;
        };
    }, [t]);
    useEffect(() => {
        const id = ++locationRequest.current;
        if (!clientId) {
            setLocationOptions([]);
            if (locationField.value)
                locationField.onChange("");
            return;
        }
        setLoadingLocations(true);
        clientsApi
            .getLocationOptions(clientId)
            .then((response) => {
            var _a;
            if (id !== locationRequest.current)
                return;
            const rows = (_a = response.data.data) !== null && _a !== void 0 ? _a : [];
            const taken = {};
            rows.forEach((l) => {
                if (l.accessPointUid && l.accessPointUid !== (ap === null || ap === void 0 ? void 0 : ap.accessPointUID))
                    taken[l.id] = l.accessPointName || l.accessPointUid;
            });
            setUsedBy(taken);
            const options = rows.map((l) => ({
                value: l.id,
                label: taken[l.id]
                    ? t("common:catalog.ap.locationUsedBy", { location: l.name, ap: taken[l.id], defaultValue: "{{location}} (used by {{ap}})" })
                    : l.name,
                disabled: Boolean(taken[l.id]),
            }));
            setLocationOptions(options);
            // A location of the previous client must not stay selected.
            if (locationField.value && !options.some((o) => o.value === locationField.value))
                locationField.onChange("");
        })
            .catch(() => id === locationRequest.current && setLocationOptions([]))
            .finally(() => id === locationRequest.current && setLoadingLocations(false));
    }, [clientId]);
    const merge = (loaded, seeded) => loaded.length > 0 ? loaded : seeded;
    const seededLocation = (ap === null || ap === void 0 ? void 0 : ap.clientLocationId) && ap.clientId === clientId ? [{ value: ap.clientLocationId, label: (_a = ap.clientLocationName) !== null && _a !== void 0 ? _a : "" }] : [];
    return (React.createElement(React.Fragment, null,
        React.createElement(Typography, { variant: "h4", sx: { color: "primary.darker" } }, t("common:catalog.ap.clientAndCategories", "Client & Categories")),
        React.createElement(Grid, { container: true, spacing: 1.5, sx: { mb: 2 } },
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FSelect, { name: "clientId", control: control, label: t("common:catalog.clients.client", "Client"), options: merge(clientOptions, seededClients) })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FSelect, { name: "clientLocationId", control: control, label: t("common:catalog.clients.location", "Location"), options: loadingLocations ? seededLocation : merge(locationOptions, seededLocation), disabled: !clientId, loading: loadingLocations, required: Boolean(clientId), getOptionDisabled: (option) => Boolean(option.disabled), rules: {
                        validate: (value, values) => {
                            if (!values.clientId)
                                return true;
                            if (!value)
                                return t("common:catalog.ap.locationRequired", "Select a location for the client.");
                            return usedBy[String(value)]
                                ? t("common:catalog.ap.locationInUse", "This location is already assigned to another access point.")
                                : true;
                        },
                    }, noOptionsText: t("common:catalog.ap.noLocations", "This client has no locations") })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FSelect, { name: "apCategoryId", control: control, label: t("common:catalog.categories.categoryLabel", "Category"), options: merge(categoryOptions, seededCategories) })))));
};
